import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../api/client";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Plus, Trash, User, Package, Coins } from "@phosphor-icons/react";
import { BRAND } from "../Brand";

const TYPE_ICON = { work: User, material: Package, cost: Coins };
const TYPE_LABEL = { work: "Work", material: "Material", cost: "Cost" };

export default function ResourcesTab({ projectId, canEdit }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState({ name: "", type: "work", rate: "", rate_unit: "day", max_units_pct: "100" });

  const load = useCallback(async () => {
    if (!projectId) { setRows([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/plan/resources", { params: { project_id: projectId } });
      setRows(data.resources || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!draft.name.trim()) return;
    try {
      await api.post("/plan/resources", {
        project_id: projectId,
        name: draft.name.trim(),
        type: draft.type,
        rate: Number(draft.rate || 0),
        rate_unit: draft.rate_unit,
        max_units_pct: Number(draft.max_units_pct || 100),
      });
      setDraft({ name: "", type: "work", rate: "", rate_unit: "day", max_units_pct: "100" });
      load();
      toast.success("Resource added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const patch = async (r, field, value) => {
    try {
      await api.patch(`/plan/resources/${r.id}`, { [field]: value });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete resource "${r.name}"? Any task assignments will be removed too.`)) return;
    try {
      await api.delete(`/plan/resources/${r.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="flex-1 overflow-auto p-6" data-testid="resources-tab">
      <div className="max-w-5xl">
        {canEdit ? (
          <form onSubmit={create} className="bg-white border border-neutral-200 rounded-xl p-4 mb-5 grid grid-cols-6 gap-3 items-end">
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Name</label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="resource-name-input" className="h-9 mt-1" placeholder="Resource name" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Type</label>
              <Select value={draft.type} onValueChange={(v) => setDraft({ ...draft, type: v })}>
                <SelectTrigger className="h-9 mt-1" data-testid="resource-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="work">Work (people)</SelectItem>
                  <SelectItem value="material">Material</SelectItem>
                  <SelectItem value="cost">Cost (flat fee)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Rate</label>
              <Input type="number" min="0" value={draft.rate} onChange={(e) => setDraft({ ...draft, rate: e.target.value })} data-testid="resource-rate-input" className="h-9 mt-1" placeholder="0" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Per</label>
              <Select value={draft.rate_unit} onValueChange={(v) => setDraft({ ...draft, rate_unit: v })}>
                <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Day</SelectItem>
                  <SelectItem value="hour">Hour</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Max %</label>
              <Input type="number" min="1" value={draft.max_units_pct} onChange={(e) => setDraft({ ...draft, max_units_pct: e.target.value })} className="h-9 mt-1" data-testid="resource-max-input" />
            </div>
            <div className="col-span-6">
              <Button type="submit" className="text-white h-9" style={{ backgroundColor: BRAND.colors.blue }} data-testid="resource-add-btn">
                <Plus size={13} weight="bold" className="mr-1.5" /> Add resource
              </Button>
            </div>
          </form>
        ) : null}

        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="resources-table">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-[10px] uppercase tracking-widest text-neutral-500">
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2 w-28">Type</th>
                <th className="text-right px-3 py-2 w-32">Rate</th>
                <th className="text-right px-3 py-2 w-20">Max %</th>
                {canEdit ? <th className="w-10"></th> : null}
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8 text-neutral-400 text-sm">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8 text-neutral-400 text-sm">No resources yet. Add people or material above.</td></tr>
              ) : rows.map((r) => {
                const Icon = TYPE_ICON[r.type] || User;
                return (
                  <tr key={r.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`resource-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Icon size={14} weight="bold" className="text-neutral-500" />
                        {canEdit ? (
                          <input
                            defaultValue={r.name}
                            onBlur={(e) => e.target.value.trim() !== r.name && patch(r, "name", e.target.value.trim())}
                            className="bg-transparent border-b border-transparent hover:border-neutral-300 focus:border-neutral-500 outline-none w-full"
                          />
                        ) : <span>{r.name}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-neutral-600">{TYPE_LABEL[r.type] || r.type}</td>
                    <td className="px-3 py-2 text-right font-mono text-neutral-700">
                      {r.rate?.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 })}
                      <span className="text-neutral-400 text-[10px] ml-1">/{r.rate_unit}</span>
                    </td>
                    <td className="px-3 py-2 text-right text-neutral-700">{r.max_units_pct}%</td>
                    {canEdit ? (
                      <td className="px-2 py-2 text-right">
                        <button onClick={() => remove(r)} className="text-red-500 hover:text-red-700" data-testid={`resource-delete-${r.id}`}>
                          <Trash size={13} weight="bold" />
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
